<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { McTypeahead, McTooltip } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import {
  FilterType,
  FilterTypeEnum,
  useApplyFilter,
  useLookupFetcher,
  FilterDefinition,
  formatOptionLabel
} from "@/features/filters";
import { normalisedText } from "@/global";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const hasUserCleared = ref(false);

const {
  key: filterKey,
  label: filterLabel = "",
  placeholder = "",
  hiddenlabel,
  lookupUrl,
  config,
  dependencies,
  options: optionsProp,
  sectionLabel,
  groupId,
  groupLabel,
  searchParam,
  debounce,
  maxOptions,
  minChars,
  labelTooltip = false,
  clearButton,
  inputParams
} = props.filter;

const { t } = useI18n();

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const searchText = ref("");
const typedLabel = ref("");
const zone = props.zonePrefix || "default";

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;

  const storeValue = source?.[zone]?.[filterKey];

  return {
    ...(storeValue || { label: "", value: "" }),
    filterType: props.filterType
  };
});

const inputLabel = computed(() => {
  if (typedLabel.value !== "") return typedLabel.value;
  if (hasUserCleared.value) return "";
  return selectedFilter.value?.label || "";
});

const resolvedDependencies = computed(() => {
  const deps: Record<string, any> = {};
  if (!dependencies) return deps;

  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;

  for (const [param, dependsOn] of Object.entries(dependencies)) {
    const dep = source?.[zone]?.[dependsOn];
    deps[param] = typeof dep === "object" ? dep?.value : dep;
  }

  return deps;
});

/**
 * Clear dependent filters when the current filter is changed/updated
 */
const clearDependencies = () => {
  if (props.filter.clearDependencies) {
    props.filter.clearDependencies?.forEach((dependentFilterKey) => {
      removeFilter(dependentFilterKey);
    });
  }
};

const {
  options: fetchedOptions,
  fetch,
  loading
} = useLookupFetcher(
  filterKey,
  lookupUrl || "",
  config || {},
  { forceRefresh: false, autoFetchEnabled: false, clearOptionsOnEmptyInput: true },
  resolvedDependencies,
  inputParams,
  searchParam,
  searchText
);

const options = computed(() => {
  const rawOptions = optionsProp || fetchedOptions.value;
  const formatTemplate = props.filter.optionLabelFormat;
  const normaliseText = props.filter.normaliseText;
  const subLabelKey = props.filter.subLabelKey;

  if (!formatTemplate && !normaliseText)
    return rawOptions.map((opt) => ({ ...opt, ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}) }));

  return rawOptions.map((opt) => ({
    ...opt,
    ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}),
    ...(formatTemplate && { label: formatOptionLabel(opt, formatTemplate) || opt.label }),
    ...(normaliseText && { label: normalisedText(opt.label) || opt.label })
  }));
});

const onInput = (e: any) => {
  const text = e?.target?.value || "";
  searchText.value = text;
  typedLabel.value = text;
  hasUserCleared.value = text.trim() === "";

  if (hasUserCleared.value) {
    removeFilter(filterKey);
    clearDependencies();
  }
};

const onSelect = (event: any) => {
  const value = event?.detail?.value;
  const selectedOption = options.value.find((option) => option.value === value);
  if (!selectedOption) return;

  // clear dependency only if the current filter is updated
  if (selectedFilter.value.value !== selectedOption.value) {
    clearDependencies();
  }

  applyFilter(filterKey, {
    filterKey,
    label: selectedOption.label,
    value,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {}),
    ...(props.filter.clearDependencies ? { clearDependencies: props.filter.clearDependencies } : {})
  });
};

const onSearch = () => {
  fetch();
};

const onClear = () => {
  removeFilter(filterKey);
  clearDependencies();
};
</script>

<template>
  <mc-tooltip fit="small">
    <mc-typeahead
      slot="trigger"
      data-test="typeahead-filter"
      :name="filterKey"
      fit="small"
      trailingicon="magnifying-glass"
      :label="t(filterLabel)"
      :placeholder="t(placeholder)"
      :hiddenlabel="hiddenlabel"
      :data="options"
      :value="inputLabel"
      :loading="!optionsProp && loading"
      :debounce="debounce || 300"
      :maxoptions="maxOptions || 10"
      :min-chars="minChars || 3"
      :clearbutton="clearButton"
      highlight
      showlistonfocus
      @input="onInput"
      @optionselected="onSelect"
      @search="onSearch"
      @clearbuttonclick="onClear"
    />
    <span v-if="labelTooltip && inputLabel">{{ t(filterLabel) }}</span>
  </mc-tooltip>
</template>
